import { Loader2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { SearchInput } from '@/components/common/SearchInput';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectIcon,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { DROPDOWN_ITEM_CLASS, DROPDOWN_PANEL_CLASS } from '@/styles/floatingPanel';
import { formatCount } from '@/utils/number';
import { PermissionMatrixUnit } from '../components/PermissionMatrixUnit';
import { usePermissionsSetup } from '../hooks/usePermissionsSetup';

interface PermissionsSetupPageProps {
  userId?: number | undefined;
}

export function PermissionsSetupPage({ userId }: PermissionsSetupPageProps) {
  const setup = usePermissionsSetup(userId);

  return (
    <>
      <div className={FIT_TO_SCREEN_PAGE_CLASS}>
        <PageHeader title={setup.userName ? `Permissions of ${setup.userName}` : 'Permissions'} />

        <div className="flex flex-wrap items-center gap-3 rounded-xl bg-card px-4 py-3 ring-1 ring-primary/12 sm:px-5">
          {setup.isUserMode ? null : (
            <Select<string>
              items={setup.roles.map((role) => ({ value: String(role.id), label: role.name }))}
              value={setup.roleId === null ? '' : String(setup.roleId)}
              onValueChange={(next) => setup.selectRole(next ? Number(next) : null)}
            >
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Pick a role" />
                <SelectIcon />
              </SelectTrigger>
              <SelectContent className={DROPDOWN_PANEL_CLASS}>
                {setup.roles.map((role) => (
                  <SelectItem key={role.id} value={String(role.id)} className={DROPDOWN_ITEM_CLASS}>
                    {role.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <div className="min-w-56 flex-1">
            <SearchInput
              value={setup.searchText}
              onChange={setup.setSearchText}
              placeholder="Find a menu or page"
            />
          </div>

          <Button type="button" variant="outline" size="sm" onClick={setup.expandAll}>
            Expand all
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={setup.collapseAll}>
            Collapse all
          </Button>
        </div>

        {setup.isUserMode ? (
          <p className="text-sm text-muted-foreground">
            Holds{' '}
            {setup.userRoleNames.length === 0
              ? 'no role'
              : setup.userRoleNames.map((name) => `${name} Role`).join(' \u00b7 ')}
            {'. '}
            Cells naming a role cannot be changed here. Ticks are this user&apos;s own.
          </p>
        ) : null}

        {setup.isLocked ? (
          <p className="text-sm text-muted-foreground">
            {setup.role?.name} holds the whole catalogue: it is what lets someone grant the rest.
          </p>
        ) : null}

        <div className="min-h-0 flex-1 overflow-y-auto">
          {setup.error ? (
            <ErrorState error={setup.error} fallbackTitle="Could not load the permissions" />
          ) : setup.isLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : !setup.hasTarget ? (
            <EmptyState
              kind="noData"
              title="Pick a role"
              description="Choose a role above to see and change what it may do."
            />
          ) : setup.units.length === 0 ? (
            <EmptyState kind="noResults" title="No menu matches that search" />
          ) : (
            <div className="flex flex-col gap-3">
              {setup.units.map((unit, index) => (
                <PermissionMatrixUnit
                  key={unit.unitKey}
                  unit={unit}
                  index={index + 1}
                  isCollapsed={setup.isCollapsed(unit.unitKey)}
                  isLocked={setup.isLocked}
                  selectedIds={setup.selectedIds}
                  coveredBy={setup.coveredBy}
                  onToggleUnit={() => setup.toggleUnit(unit.unitKey)}
                  onToggle={setup.toggle}
                  onGrant={(action, shouldGrant) => setup.grantUnit(unit, action, shouldGrant)}
                />
              ))}
            </div>
          )}
        </div>

        {setup.isLocked || !setup.hasTarget ? null : (
          <div className="flex items-center gap-2 rounded-xl bg-card px-4 py-3 ring-1 ring-primary/12 sm:px-5">
            <Button
              type="button"
              onClick={setup.openConfirm}
              disabled={!setup.isDirty || setup.isSaving}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={setup.reset}
              disabled={!setup.isDirty || setup.isSaving}
            >
              Undo
            </Button>
            <span className="text-sm text-muted-foreground">
              {setup.isDirty
                ? `${formatCount(setup.addedCount, 'permission')} granted, ${formatCount(
                    setup.removedCount,
                    'permission',
                  )} revoked`
                : 'No change yet'}
            </span>
          </div>
        )}
      </div>

      <ConfirmDialog
        open={setup.isConfirmOpen}
        onOpenChange={(next) => (next ? setup.openConfirm() : setup.closeConfirm())}
        title="Save these permissions?"
        description={`${formatCount(setup.addedCount, 'permission')} granted and ${formatCount(
          setup.removedCount,
          'permission',
        )} revoked. This changes what ${
          setup.isUserMode
            ? setup.userName
            : formatCount(setup.role?.assignedUserCount ?? 0, 'user')
        } can do straight away.`}
        confirmLabel="Save"
        isPending={setup.isSaving}
        pendingLabel="Saving"
        onConfirm={setup.save}
      />
    </>
  );
}
